(function (scope) {
  'use strict';
  const fixtureTypes = new Set(['tabletop', 'two_way', 'four_way']);
  function validQuantity(quantity, pack, minimum, maximum) {
    return Number.isSafeInteger(quantity) && quantity > 0 && Number.isSafeInteger(pack) && pack > 0 && quantity % pack === 0 && quantity >= minimum && (maximum == null || quantity <= maximum);
  }
  function stepQuantity(current, direction, pack, minimum, maximum) {
    const first = Math.ceil(minimum / pack) * pack;
    const last = maximum == null ? Infinity : Math.floor(maximum / pack) * pack;
    if (last < first) return 0;
    const value = Number.isFinite(current) ? Math.max(0, current) : 0;
    if (direction > 0) return Math.min(last, Math.max(first, (Math.floor(value / pack) + 1) * pack));
    const next = (Math.ceil(value / pack) - 1) * pack;
    return next < first ? 0 : Math.min(last, next);
  }
  function packsToUnits(packs, pack, minimum = pack, maximum = null) {
    const quantity = packs * pack;
    if (!Number.isSafeInteger(packs) || packs < 0 || !Number.isSafeInteger(pack) || pack < 1) return null;
    if (packs === 0) return 0;
    return validQuantity(quantity, pack, minimum, maximum) ? quantity : null;
  }
  function qualifyingSpend(cart, fixtures, fixtureType = 'tabletop') {
    const ids = new Set(fixtures.map(f => Number(f.productId)));
    const lines = cart.items || [];
    const eligible = lines.filter(item => {
      if (ids.has(Number(item.product_id))) return false;
      const type = String(item.product_type || '').toLowerCase();
      const title = String(item.product_title || item.title || '').toLowerCase();
      const socks = type.includes('sock') || /\bsocks?\b/.test(title);
      const towels = type.includes('towel') || /\btowels?\b/.test(title);
      return socks || (fixtureType !== 'tabletop' && towels);
    });
    const eligibleSubtotal = eligible.reduce((total, item) => total + Number(item.final_line_price || 0), 0);
    const lineSubtotal = lines.reduce((total, item) => total + Number(item.final_line_price || 0), 0);
    const orderDiscount = Math.max(0, lineSubtotal - Number(cart.total_price || 0));
    return Math.max(0, eligibleSubtotal - orderDiscount);
  }
  function parseSkuLines(text) {
    const rows = String(text).replace(/^\uFEFF/, '').trim().split(/\r?\n/).filter(line => line.trim());
    if (rows.length && /^\s*"?sku"?\s*[,\t]\s*"?(packs|quantity|qty)"?\s*$/i.test(rows[0])) rows.shift();
    if (!rows.length || rows.length > 100) throw new Error('sku_invalid');
    const merged = new Map();
    for (const row of rows) {
      const match = row.match(/^\s*"?([^",\t\n]+)"?\s*[,\t]\s*"?(\d+)"?\s*$/);
      if (!match) throw new Error('sku_invalid');
      const sku = match[1].trim();
      const quantity = Number(match[2]);
      if (!sku || sku.length > 100 || !Number.isSafeInteger(quantity) || quantity < 1 || quantity > 100000) throw new Error('sku_invalid');
      merged.set(sku, (merged.get(sku) || 0) + quantity);
    }
    return [...merged].map(([sku, quantity]) => ({sku, quantity}));
  }
  function receivedTypes(raw) {
    try {
      const value = typeof raw === 'string' ? JSON.parse(raw || '[]') : raw;
      return new Set((Array.isArray(value) ? value : []).map(v => typeof v === 'string' ? v : v.type).filter(v => fixtureTypes.has(v)));
    } catch { return new Set(); }
  }
  const utilities = {packsToUnits, stepQuantity, validQuantity, qualifyingSpend, parseSkuLines, receivedTypes};
  if (typeof module !== 'undefined' && module.exports) module.exports = utilities;
  if (!scope.document) return;
  const document = scope.document;
  function init() {
    const context = document.getElementById('lavley-wholesale-context');
    if (!context) return;
    let config;
    try { config = JSON.parse(context.textContent); } catch { return; }
    const text = config.text || {};
    const translate = (key, values = {}) => String(text[key] || key).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => values[name] == null ? '' : String(values[name]));
    const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style:'currency', currency:config.currency || 'USD'}).format(Number(cents || 0) / 100);
    const root = String(config.root || '/').replace(/\/$/, '') + '/';
    const originalFetch = scope.fetch.bind(scope);
    let cartState = null;
    let cartRefreshTimer;
    let toastTimer;
    let mutationQueue = Promise.resolve();
    let activeDialog = null;
    const received = new Set([...receivedTypes(config.receivedCompany), ...receivedTypes(config.receivedCustomer)]);
    const savedKey = 'lavley-wholesale-assortments-v1:' + (config.companyId || config.customerId || 'guest');
    function el(tag, className, value) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (value != null) element.textContent = value;
      return element;
    }
    function toast(message) {
      const output = document.querySelector('[data-lw-toast]');
      if (!output) return;
      output.textContent = message;
      output.hidden = false;
      scope.clearTimeout(toastTimer);
      toastTimer = scope.setTimeout(() => { output.hidden = true; }, 6000);
    }
    async function request(path, options = {}) {
      const response = await originalFetch(root + path, {credentials:'same-origin', headers:{'Accept':'application/json', ...(options.body ? {'Content-Type':'application/json'} : {})}, ...options});
      let data;
      try { data = await response.json(); } catch { throw new Error(translate('error')); }
      if (!response.ok) throw new Error(data.description || data.message || translate('error'));
      return data;
    }
    function serial(action) {
      const next = mutationQueue.catch(() => {}).then(action);
      mutationQueue = next;
      return next;
    }
    scope.fetch = async function (...args) {
      const response = await originalFetch(...args);
      const address = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      if (response.ok && /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(address)) {
        scope.clearTimeout(cartRefreshTimer);
        cartRefreshTimer = scope.setTimeout(() => refreshCart().catch(() => {}), 100);
      }
      return response;
    };
    async function refreshCart() {
      let cart = await request('cart.js');
      const selected = selectedFixture(cart);
      const fixture = config.fixtures.find(item => item.type === selected);
      if (fixture && (received.has(selected) || qualifyingSpend(cart, config.fixtures, fixture.type) < fixture.threshold)) {
        cart = await request('cart/update.js', {method:'POST',body:JSON.stringify({attributes:{'Lavley Fixture Request':'','Lavley Fixture Review':'','Lavley Fixture Program':''}})});
      }
      cartState = cart;
      renderCart(cart);
      return cart;
    }
    function selectedFixture(cart) {
      const type = cart.attributes?.['Lavley Fixture Request'];
      return fixtureTypes.has(type) ? type : null;
    }
    function renderCart(cart) {
      const spendFor = fixture => qualifyingSpend(cart, config.fixtures, fixture.type);
      const moreFor = fixture => translate(fixture.type === 'tabletop' ? 'add_more' : 'add_more_floor', {amount:money(fixture.threshold - spendFor(fixture))});
      const chosen = selectedFixture(cart);
      document.querySelectorAll('[data-lw-cart-count]').forEach(node => { node.textContent = String(cart.item_count || 0); });
      const dock = document.querySelector('[data-lw-order-dock]');
      if (dock) {
        dock.hidden = !cart.item_count;
        dock.querySelector('[data-lw-order-total]').textContent = money(cart.total_price);
        dock.querySelector('[data-lw-order-units]').textContent = `${cart.item_count} ${translate('units')}`;
        const available = config.fixtures.filter(f => !received.has(f.type));
        const reached = available.filter(f => spendFor(f) >= f.threshold);
        const highest = Math.max(0, ...reached.map(f => f.threshold));
        const next = available.filter(f => f.threshold > highest && spendFor(f) < f.threshold).sort((a,b) => (a.threshold - spendFor(a)) - (b.threshold - spendFor(b)))[0];
        const spend = next ? spendFor(next) : reached.length ? spendFor(reached[reached.length - 1]) : 0;
        const progress = dock.querySelector('[data-lw-progress]');
        progress.max = next ? next.threshold : config.fixtures[config.fixtures.length - 1].threshold;
        progress.value = Math.min(spend, progress.max);
        dock.querySelector('[data-lw-progress-label]').textContent = next ? `${next.name}: ${moreFor(next)}` : translate('eligible');
      }
      document.querySelectorAll('[data-lw-fixture]').forEach(card => {
        const fixture = config.fixtures.find(f => f.type === card.dataset.lwFixture);
        if (!fixture) return;
        const status = card.querySelector('[data-lw-fixture-status]');
        const button = card.querySelector('[data-lw-request]');
        const claimed = received.has(fixture.type);
        const qualified = spendFor(fixture) >= fixture.threshold;
        button.disabled = claimed || (config.approved && !qualified);
        if (claimed) { status.textContent = translate('already_received'); button.textContent = translate('already_received'); }
        else if (!config.signedIn) { status.textContent = translate('needs_sign_in'); button.textContent = translate('sign_in'); }
        else if (!config.approved) { status.textContent = translate('needs_approval'); button.textContent = translate('contact'); }
        else if (chosen === fixture.type) { status.textContent = translate('review_required'); button.textContent = translate('requested'); }
        else if (!qualified) { status.textContent = moreFor(fixture); button.textContent = translate('request'); }
        else { status.textContent = fixture.available ? translate('request_note') : translate('availability_confirm'); button.textContent = translate('request'); }
      });
      document.querySelectorAll('[data-lw-request-summary]').forEach(summary => {
        summary.hidden = !chosen;
        const fixture = config.fixtures.find(f => f.type === chosen);
        summary.querySelector('[data-lw-selected-fixture]').textContent = fixture ? fixture.name : '';
      });
      document.querySelectorAll('[data-lw-cart-fixtures]').forEach(container => {
        container.replaceChildren();
        config.fixtures.forEach(fixture => {
          const spend = spendFor(fixture);
          const row = el('div', 'lw-cart-fixture');
          const description = el('div');
          description.append(el('strong', '', fixture.name));
          description.append(el('small', '', translate(fixture.type === 'tabletop' ? 'qualifying_order' : 'qualifying_order_floor', {amount:money(fixture.threshold)})));
          description.append(el('small', '', received.has(fixture.type) ? translate('already_received') : spend < fixture.threshold ? moreFor(fixture) : !fixture.available ? translate('availability_confirm') : translate('eligible')));
          const button = el('button', 'lw-button lw-button-outline', chosen === fixture.type ? translate('requested') : translate('request'));
          button.type = 'button'; button.dataset.lwRequest = fixture.type;
          button.disabled = received.has(fixture.type) || (config.approved && spend < fixture.threshold);
          row.append(description, button); container.append(row);
        });
        const remove = el('button', 'lw-text-link', translate('no_fixture'));
        remove.type = 'button'; remove.dataset.lwRequest = 'none'; container.append(remove);
      });
    }
    async function chooseFixture(type) {
      if (!config.signedIn) { scope.location.assign(config.accountUrl); return; }
      if (!config.approved) { scope.location.assign(root + 'pages/contact'); return; }
      if (type !== 'none' && !fixtureTypes.has(type)) return;
      await serial(async () => {
        const cart = await refreshCart();
        const fixture = config.fixtures.find(f => f.type === type);
        if (fixture && (received.has(type) || qualifyingSpend(cart, config.fixtures, fixture.type) < fixture.threshold)) throw new Error(translate(received.has(type) ? 'already_received' : 'quantity_error'));
        const attributes = {
          'Lavley Fixture Request':type === 'none' ? '' : type,
          'Lavley Fixture Review':type === 'none' ? '' : 'PENDING_COMPANY_HISTORY_AND_STOCK_REVIEW',
          'Lavley Fixture Program':type === 'none' ? '' : '2026-dollar-tiers-v2-socks-towels'
        };
        cartState = await request('cart/update.js', {method:'POST', body:JSON.stringify({attributes})});
        renderCart(cartState);
        toast(translate(type === 'none' ? 'request_removed' : 'requested'));
      });
    }
    function busy(button, action) {
      if (!button || button.disabled) return Promise.resolve();
      const previous = button.textContent;
      button.disabled = true;
      button.textContent = translate('working');
      return Promise.resolve().then(action).catch(error => { toast(error.message || translate('error')); }).finally(() => { button.disabled = false; button.textContent = previous; if(cartState) renderCart(cartState); });
    }
    async function addItems(items) {
      if (!config.approved) throw new Error(translate('needs_approval'));
      if (!items.length) throw new Error(translate('empty_selection'));
      try {
        await serial(() => request('cart/add.js', {method:'POST', body:JSON.stringify({items:items.map(item => ({id:Number(item.id), quantity:Number(item.quantity)}))})}));
      } catch (error) { await refreshCart().catch(() => {}); throw error; }
      await refreshCart();
      toast(translate('add_success'));
    }
    function orderRowQuantity(row, input) {
      const pack = Number(row.dataset.case);
      const minimum = Number(row.dataset.minimum) || pack;
      const countsPacks = input.dataset.lwQuantityMode === 'packs';
      const factor = countsPacks ? pack : 1;
      const maximum = input.hasAttribute('max') ? Number(input.max) * factor : null;
      const entered = input.value.trim() === '' ? NaN : Number(input.value);
      const quantity = entered * factor;
      const valid = countsPacks ? packsToUnits(entered, pack, minimum, maximum) !== null :
        quantity === 0 || validQuantity(quantity, pack, minimum, maximum);
      return {quantity, pack, minimum, maximum, valid};
    }
    function rowSelection(form) {
      const items = [];
      for (const row of form.querySelectorAll('[data-lw-order-row]')) {
        const input = row.querySelector('[data-lw-quantity]');
        const {quantity, pack, minimum, valid} = orderRowQuantity(row, input);
        if (!valid) { input.focus(); throw new Error(translate('pack_count_error')); }
        if (!quantity) continue;
        if (input.disabled) { input.focus(); throw new Error(translate('quantity_error')); }
        items.push({id:Number(row.dataset.variant), quantity, pack, minimum, sku:row.dataset.sku, title:row.dataset.title, handle:row.dataset.handle});
      }
      return items;
    }
    function updateSelection(form) {
      let packs = 0; let units = 0; let total = 0;
      form.querySelectorAll('[data-lw-order-row]').forEach(row => {
        const input = row.querySelector('[data-lw-quantity]');
        const {quantity, pack, minimum, maximum, valid} = orderRowQuantity(row, input);
        input.setCustomValidity(valid ? '' : translate(input.dataset.lwQuantityMode === 'packs' ? 'pack_count_error' : 'quantity_multiple_error', {count:pack}));
        input.setAttribute('aria-invalid', String(!valid));
        const decrease = row.querySelector('[data-lw-step="-1"]');
        const increase = row.querySelector('[data-lw-step="1"]');
        if (decrease) decrease.disabled = input.disabled || !Number.isFinite(quantity) || quantity <= 0;
        if (increase) increase.disabled = input.disabled || stepQuantity(quantity, 1, pack, minimum, maximum) <= quantity;
        const selectedQuantity = valid && !input.disabled ? quantity : 0;
        const line = selectedQuantity * Number(row.dataset.price);
        packs += selectedQuantity / pack; units += selectedQuantity; total += line;
        row.querySelector('[data-lw-line-total]').textContent = config.approved ? money(line) : '—';
      });
      const outputPacks = form.querySelector('[data-lw-selected-packs]');
      const outputUnits = form.querySelector('[data-lw-selected-units]');
      const outputTotal = form.querySelector('[data-lw-selected-total]');
      if (outputPacks) outputPacks.textContent = String(packs);
      if (outputUnits) outputUnits.textContent = String(units);
      if (outputTotal) outputTotal.textContent = money(total);
    }
    function getSaved() {
      if (!config.signedIn) return [];
      try { const saved = JSON.parse(scope.localStorage.getItem(savedKey) || '[]'); return Array.isArray(saved) ? saved.slice(0,30) : []; } catch { return []; }
    }
    function putSaved(saved) {
      scope.localStorage.setItem(savedKey, JSON.stringify(saved));
      renderSaved();
    }
    function makeDialog(title, description) {
      if (activeDialog) activeDialog.close();
      const dialog = el('dialog', 'lw-dialog');
      const heading = el('h2', '', title); heading.id = 'lw-dialog-title';
      dialog.setAttribute('aria-labelledby', heading.id);
      dialog.append(heading);
      if (description) dialog.append(el('p', '', description));
      document.body.append(dialog);
      dialog.addEventListener('close', () => { dialog.remove(); if (activeDialog === dialog) activeDialog = null; });
      activeDialog = dialog;
      return dialog;
    }
    async function saveCart() {
      if (!config.signedIn) { scope.location.assign(config.accountUrl); return; }
      const cart = await refreshCart();
      const items = cart.items.filter(item => !config.fixtures.some(f => Number(f.productId) === Number(item.product_id)));
      if (!items.length) throw new Error(translate('empty_selection'));
      const dialog = makeDialog(translate('save'), translate('saved_help'));
      const form = el('form');
      const field = el('div', 'lw-field');
      const label = el('label', '', translate('saved_name')); label.htmlFor = 'lw-assortment-name';
      const input = el('input'); input.id = 'lw-assortment-name'; input.required = true; input.maxLength = 80;
      field.append(label,input);
      const actions = el('div', 'lw-actions');
      const cancel = el('button', 'lw-button lw-button-outline', translate('cancel')); cancel.type = 'button'; cancel.addEventListener('click', () => dialog.close());
      const save = el('button', 'lw-button', translate('save')); save.type = 'submit';
      actions.append(cancel,save); form.append(field,actions); dialog.append(form);
      form.addEventListener('submit', event => {
        event.preventDefault();
        const name = input.value.trim(); if (!name) return;
        try {
          const saved = getSaved();
          saved.unshift({id:scope.crypto.randomUUID(), name, date:new Date().toISOString(), items:items.map(item => ({id:item.variant_id, quantity:item.quantity, handle:item.handle, sku:item.sku, title:item.product_title || item.title, pack:item.quantity_rule?.increment || 1}))});
          putSaved(saved.slice(0,30)); dialog.close(); toast(translate('saved_confirmation'));
        } catch { toast(translate('error')); }
      });
      dialog.showModal(); input.focus();
    }
    function renderSaved() {
      document.querySelectorAll('[data-lw-saved-list]').forEach(container => {
        container.replaceChildren();
        if (!config.signedIn) { const link = el('a','lw-text-link',translate('signed_out_saved')); link.href=config.accountUrl; container.append(link); return; }
        const saved = getSaved();
        if (!saved.length) { container.append(el('p', 'lw-fine-print', translate('saved_empty'))); return; }
        saved.forEach(assortment => {
          const card = el('article', 'lw-saved-card');
          card.append(el('h3', '', assortment.name));
          card.append(el('small', '', `${Array.isArray(assortment.items) ? assortment.items.length : 0} SKUs · ${translate('device_note')}`));
          const actions = el('div','lw-actions');
          const load = el('button','lw-button lw-button-outline',translate('load_review')); load.type='button';
          load.addEventListener('click', () => busy(load, () => reviewSaved(assortment)));
          const remove = el('button','lw-text-link',translate('delete')); remove.type='button';
          remove.addEventListener('click', () => { if (scope.confirm(translate('confirm_delete'))) putSaved(getSaved().filter(item => item.id !== assortment.id)); });
          actions.append(load,remove); card.append(actions); container.append(card);
        });
      });
    }
    async function getProduct(handle) {
      if (!/^[a-zA-Z0-9_-]+$/.test(handle || '')) throw new Error(translate('not_found'));
      return request('products/' + encodeURIComponent(handle) + '?view=wholesale-data');
    }
    async function reviewSaved(assortment) {
      if (!config.approved) throw new Error(translate('needs_approval'));
      const items = [];
      const products = new Map();
      for (const item of (assortment.items || []).slice(0,100)) {
        let product = products.get(item.handle);
        if (!product) { try { product = await getProduct(item.handle); } catch {} if(product) products.set(item.handle, product); }
        const variant = product?.variants?.find(v => Number(v.id) === Number(item.id));
        items.push({...item, price:variant?.price || 0, available:!!variant?.available, pack:variant?.quantity_rule?.increment || Number(item.pack) || 1, minimum:variant?.quantity_rule?.min || 1, maximum:variant?.quantity_rule?.max ?? null});
      }
      showReview(items, translate('load_review'));
    }
    function showReview(items, title) {
      const dialog = makeDialog(title, translate('saved_review_help'));
      const table = el('table');
      const head = el('tr'); [translate('sku'),translate('all_products'),translate('packs'),translate('line_total')].forEach(value=>head.append(el('th','',value)));
      const thead=el('thead');thead.append(head);table.append(thead);
      const tbody=el('tbody'); const controls=[];
      items.forEach(item => {
        const row=el('tr');row.append(el('td','',item.sku || '—'));
        const product=el('td','',item.title || item.sku); product.append(el('small','lw-fine-print',item.available ? '' : ' · '+translate('not_found'))); row.append(product);
        const quantityCell=el('td');const input=el('input');input.type='number';input.min='0';input.step='1';if(item.maximum!=null)input.max=String(Math.floor(item.maximum/(item.pack||1)));input.value=String(item.available ? item.quantity/(item.pack||1) : 0);input.disabled=!item.available;input.setAttribute('aria-label',translate('packs')+' '+(item.sku || item.title));quantityCell.append(input);row.append(quantityCell);
        const line=el('td','',money(item.available ? item.price*item.quantity : 0));row.append(line);tbody.append(row);
        input.addEventListener('input',()=>{line.textContent=money(item.price*(packsToUnits(Number(input.value),Number(item.pack)||1,Number(item.minimum)||1,item.maximum??null)??0));});
        controls.push({item,input});
      });
      table.append(tbody);dialog.append(table);
      const actions=el('div','lw-actions');const cancel=el('button','lw-button lw-button-outline',translate('cancel'));cancel.type='button';cancel.addEventListener('click',()=>dialog.close());
      const add=el('button','lw-button',translate('add_reviewed'));add.type='button';
      add.addEventListener('click',()=>busy(add,async()=>{const selected=[];for(const {item,input} of controls){const quantity=packsToUnits(input.value.trim()===''?NaN:Number(input.value),Number(item.pack)||1,Number(item.minimum)||1,item.maximum??null);if(quantity===0)continue;if(input.disabled || quantity===null){input.focus();throw new Error(translate('quantity_error'));}selected.push({id:item.id,quantity});}await addItems(selected);dialog.close();}));
      actions.append(cancel,add);dialog.append(actions);dialog.showModal();
    }
    async function findSku(sku) {
      const query = new URLSearchParams({q:sku, 'resources[type]':'product', 'resources[limit]':'10', 'resources[options][unavailable_products]':'show'});
      const results = await request('search/suggest.json?' + query);
      for (const result of results.resources?.results?.products || []) {
        let product;try{product=await getProduct(result.handle);}catch{continue;}
        if(config.fixtures.some(f=>Number(f.productId)===Number(product.id)))continue;
        const variant=product.variants?.find(v=>String(v.sku||'').toLowerCase()===sku.toLowerCase());
        if(variant)return {id:variant.id,sku:variant.sku,title:product.title,handle:product.handle,price:variant.price,available:variant.available,pack:variant.quantity_rule?.increment||1,minimum:variant.quantity_rule?.min||1,maximum:variant.quantity_rule?.max??null};
      }
      return {sku,title:translate('not_found'),available:false};
    }
    function updatePackSummary(scopeNode) {
      if (!scopeNode) return;
      const input = scopeNode.querySelector('input[type=number]');
      if (!input) return;
      const quantity = Number(input.value);
      scopeNode.querySelectorAll('[data-lw-pack-summary]').forEach(output => {
        const pack = Number(output.dataset.pack);
        if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity % pack !== 0) {
          output.textContent = output.dataset.invalid;
          return;
        }
        const packs = quantity / pack;
        output.textContent = output.dataset.template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => ({quantity, unit:output.dataset.unit, packs, pack_label:packs === 1 ? output.dataset.single : output.dataset.plural, size:pack})[key] ?? '');
      });
    }
    document.querySelectorAll('[data-lw-pack-scope]').forEach(updatePackSummary);
    document.addEventListener('input', event => {
      if (event.target.matches('input[type=number]')) updatePackSummary(event.target.closest('[data-lw-pack-scope]'));
    });
    document.addEventListener('click',event=>{
      const fixtureButton=event.target.closest('[data-lw-request]');
      if(fixtureButton){event.preventDefault();busy(fixtureButton,()=>chooseFixture(fixtureButton.dataset.lwRequest));return;}
      const save=event.target.closest('[data-lw-save-cart]');
      if(save){event.preventDefault();busy(save,saveCart);}
    });
    document.querySelectorAll('[data-lw-order-form]').forEach(form=>{
      form.addEventListener('input',()=>updateSelection(form));
      form.addEventListener('change',event=>{if(event.target.matches('[data-lw-quantity]')){updateSelection(form);event.target.reportValidity();}});
      form.addEventListener('click',event=>{
        const button = event.target.closest('[data-lw-step]');
        if (!button) return;
        const row = button.closest('[data-lw-order-row]');
        const input = row.querySelector('[data-lw-quantity]');
        const {quantity, pack, minimum, maximum} = orderRowQuantity(row, input);
        const next = stepQuantity(quantity, Number(button.dataset.lwStep), pack, minimum, maximum);
        input.value = String(input.dataset.lwQuantityMode === 'packs' ? next / pack : next);
        updateSelection(form);
      });
      form.addEventListener('submit',event=>{event.preventDefault();const button=form.querySelector('button[type=submit]');busy(button,async()=>{const items=rowSelection(form);await addItems(items);form.querySelectorAll('[data-lw-quantity]').forEach(input=>{input.value='0';});updateSelection(form);});});
      updateSelection(form);
    });
    document.querySelectorAll('[data-lw-product-form]').forEach(form=>{
      form.addEventListener('submit',event=>{
        event.preventDefault();
        busy(form.querySelector('button[type=submit]'),async()=>{
          const input=form.elements.quantity;
          const quantity=Number(input.value);
          const pack=Number(form.dataset.case)||1;
          const maximum=input.hasAttribute('max')?Number(input.max):null;
          if(!validQuantity(quantity,pack,Number(form.dataset.minimum)||pack,maximum))throw new Error(translate('quantity_error'));
          await addItems([{id:Number(form.elements.id.value),quantity}]);
        });
      });
    });
    document.querySelectorAll('[data-lw-sku-form]').forEach(form=>{
      const file=form.querySelector('[data-lw-sku-file]');
      file.addEventListener('change',async()=>{if(!file.files[0])return;if(file.files[0].size>65536){toast(translate('sku_invalid'));return;}form.elements.skus.value=await file.files[0].text();});
      form.addEventListener('submit',event=>{event.preventDefault();busy(form.querySelector('button[type=submit]'),async()=>{let rows;try{rows=parseSkuLines(form.elements.skus.value);}catch{throw new Error(translate('sku_invalid'));}const items=[];for(const row of rows){const item=await findSku(row.sku);items.push({...item,quantity:row.quantity*(item.pack||1)});}showReview(items,translate('review_skus'));});});
    });
    document.addEventListener('cart:updated',()=>refreshCart().catch(()=>{}));
    document.addEventListener('shopify:section:load',()=>{renderSaved();if(cartState)renderCart(cartState);});
    renderSaved();
    refreshCart().catch(()=>{});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true}); else init();
})(typeof window !== 'undefined' ? window : globalThis);
